import { useMutation, useQuery } from "@apollo/client";
import { Intent, OverlayToaster, Position } from "@blueprintjs/core";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import {
    AddFavoriteBusMutation,
    AddFavoriteBusMutationVariables,
    GetFavoriteBusesQuery,
    RemoveFavoriteBusMutation,
    RemoveFavoriteBusMutationVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState
} from "react";
import { NiraBusId, niraBusIdToRegion } from "types/busType";

const GET_FAVORITE_BUSES = loader("src/graphql/getFavoriteBuses.graphql");
const ADD_FAVORITE_BUS = loader("src/graphql/addFavoriteBus.graphql");
const REMOVE_FAVORITE_BUS = loader("src/graphql/removeFavoriteBus.graphql");

// Both mutations write optimistically, so a silent failure looks like the star
// flipping on and then back off by itself. Say so instead.
const FavoriteBusesToaster = OverlayToaster.create({ position: Position.TOP });

type FavoriteBusesApi = {
    // This region's favorites, newest first.
    readonly favorites: ReadonlyArray<NiraBusId>;
    readonly isFavorite: (niraBusId: NiraBusId) => boolean;
    readonly toggleFavorite: (niraBusId: NiraBusId) => void;
    // Both resolve once the mutation settles, so callers can order writes to
    // the same bus. Already-favorited buses stay favorited.
    readonly addFavorite: (niraBusId: NiraBusId) => Promise<unknown>;
    readonly removeFavorite: (niraBusId: NiraBusId) => Promise<unknown>;

    // Whether the view is narrowed to this region's favorites. One flag for the
    // sidebar list AND the map
    readonly favoritesFilterOn: boolean;
    readonly setFavoritesFilterOn: (on: boolean) => void;

    readonly favoritesPanelOpen: boolean;
    readonly setFavoritesPanelOpen: (open: boolean) => void;
};

// Holds every region's favorites; useFavoriteBuses narrows to the current one.
type FavoriteBusesContextType = Omit<FavoriteBusesApi, "favorites"> & {
    readonly allFavorites: ReadonlyArray<NiraBusId>;
};

const FavoriteBusesContext = createContext<
    FavoriteBusesContextType | undefined
>(undefined);

export const FavoriteBusesProvider: React.FC<PropsWithChildren> = ({
    children
}) => {
    const [favoritesFilterOn, setFavoritesFilterOn] = useState(false);
    const [favoritesPanelOpen, setFavoritesPanelOpen] = useState(false);

    // One promise chain per bus, so a fast double-toggle cannot land its
    // insert and delete out of order. Different buses stay independent.
    const writeQueue = useRef(new Map<NiraBusId, Promise<unknown>>());

    // Row permissions scope this to the signed-in user, so it needs no
    // arguments and cannot return anyone else's rows.
    const { data } = useQuery<GetFavoriteBusesQuery>(GET_FAVORITE_BUSES);

    const allFavorites = useMemo<ReadonlyArray<NiraBusId>>(
        () => (data?.favorite_buses ?? []).map((row) => row.nira_bus_id),
        [data]
    );

    // Both mutations write the new list straight into the cache so the star
    // flips on click instead of after a round trip.
    const [addFavoriteBus] = useMutation<
        AddFavoriteBusMutation,
        AddFavoriteBusMutationVariables
    >(ADD_FAVORITE_BUS, {
        onError: (error) => {
            console.error("Failed to add favorite bus:", error);
            FavoriteBusesToaster.show({
                message: "Couldn't add that bus to your favorites.",
                intent: Intent.DANGER
            });
        },
        update: (cache, result) => {
            const added = result.data?.insert_favorite_buses_one;
            if (added == null) {
                return;
            }
            const existing = cache.readQuery<GetFavoriteBusesQuery>({
                query: GET_FAVORITE_BUSES
            }) ?? { favorite_buses: [] };
            if (
                existing.favorite_buses.some(
                    (row) => row.nira_bus_id === added.nira_bus_id
                )
            ) {
                return;
            }
            cache.writeQuery({
                query: GET_FAVORITE_BUSES,
                data: { favorite_buses: [added, ...existing.favorite_buses] }
            });
        }
    });

    const [removeFavoriteBus] = useMutation<
        RemoveFavoriteBusMutation,
        RemoveFavoriteBusMutationVariables
    >(REMOVE_FAVORITE_BUS, {
        onError: (error) => {
            console.error("Failed to remove favorite bus:", error);
            FavoriteBusesToaster.show({
                message: "Couldn't remove that bus from your favorites.",
                intent: Intent.DANGER
            });
        },
        update: (cache, result, options) => {
            if (result.data?.delete_favorite_buses == null) {
                return;
            }
            const removedId = options.variables?.niraBusId;
            const existing = cache.readQuery<GetFavoriteBusesQuery>({
                query: GET_FAVORITE_BUSES
            }) ?? { favorite_buses: [] };
            cache.writeQuery({
                query: GET_FAVORITE_BUSES,
                data: {
                    favorite_buses: existing.favorite_buses.filter(
                        (row) => row.nira_bus_id !== removedId
                    )
                }
            });
        }
    });

    const allFavoriteIds = useMemo(() => new Set(allFavorites), [allFavorites]);

    const isFavorite = useCallback(
        (niraBusId: NiraBusId): boolean => allFavoriteIds.has(niraBusId),
        [allFavoriteIds]
    );

    const addFavorite = useCallback(
        (niraBusId: NiraBusId): Promise<unknown> => {
            const next = (
                writeQueue.current.get(niraBusId) ?? Promise.resolve()
            )
                .catch(() => undefined)
                .then(() =>
                    addFavoriteBus({
                        variables: { niraBusId },
                        optimisticResponse: {
                            insert_favorite_buses_one: {
                                __typename: "favorite_buses",
                                nira_bus_id: niraBusId
                            }
                        }
                    })
                );
            writeQueue.current.set(niraBusId, next);
            return next;
        },
        [addFavoriteBus]
    );

    const removeFavorite = useCallback(
        (niraBusId: NiraBusId): Promise<unknown> => {
            const next = (
                writeQueue.current.get(niraBusId) ?? Promise.resolve()
            )
                .catch(() => undefined)
                .then(() =>
                    removeFavoriteBus({
                        variables: { niraBusId },
                        optimisticResponse: {
                            delete_favorite_buses: {
                                __typename: "favorite_buses_mutation_response",
                                returning: [
                                    {
                                        __typename: "favorite_buses",
                                        nira_bus_id: niraBusId
                                    }
                                ]
                            }
                        }
                    })
                );
            writeQueue.current.set(niraBusId, next);
            return next;
        },
        [removeFavoriteBus]
    );

    const toggleFavorite = useCallback(
        (niraBusId: NiraBusId): void => {
            if (allFavorites.includes(niraBusId)) {
                removeFavorite(niraBusId);
            } else {
                addFavorite(niraBusId);
            }
        },
        [allFavorites, addFavorite, removeFavorite]
    );

    const value = useMemo<FavoriteBusesContextType>(
        () => ({
            allFavorites,
            isFavorite,
            toggleFavorite,
            addFavorite,
            removeFavorite,
            favoritesFilterOn,
            setFavoritesFilterOn,
            favoritesPanelOpen,
            setFavoritesPanelOpen
        }),
        [
            allFavorites,
            isFavorite,
            toggleFavorite,
            addFavorite,
            removeFavorite,
            favoritesFilterOn,
            favoritesPanelOpen
        ]
    );

    return (
        <FavoriteBusesContext.Provider value={value}>
            {children}
        </FavoriteBusesContext.Provider>
    );
};

export const useFavoriteBuses = (): FavoriteBusesApi => {
    const context = useContext(FavoriteBusesContext);
    const { region } = useMapViewRoutingMetadata();
    // Rows carry no region column, so scope on the prefix of the bus id.
    const favorites = useMemo(
        () =>
            (context?.allFavorites ?? []).filter(
                (niraBusId) => niraBusIdToRegion(niraBusId) === region
            ),
        [context?.allFavorites, region]
    );
    if (context === undefined) {
        throw new Error(
            "useFavoriteBuses must be used within FavoriteBusesProvider"
        );
    }
    // allFavorites is destructured only to drop it from the returned API.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { allFavorites, ...rest } = context;
    return { ...rest, favorites };
};
